import React from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import routes from './routes/routes';
import { logger } from '@/shared/utils';
import { performanceMonitor } from '@/shared/utils/PerformanceMonitor';
import { validateEnvironment, ENV } from './config/environment';
import { ThemeMasterProvider } from '@/shared/theme';
import { UserStateProvider } from '@/features/user';
import GlobalErrorBoundary from '@/shared/components/GlobalErrorBoundary';
// Page view tracking is handled in MainLayout.tsx

// Import all styles
import './App.css';
import './index.css';
import './styles/glass-effects.css';
import './styles/theme-overrides.css';
import './styles/blog-editor.css';
import './styles/cursor-effects.css';
import './styles/rich-text-editor.css';

// Validate environment configuration on app startup
try {
  validateEnvironment();
  logger.info('Environment validated successfully', 'App', {
    appName: ENV.app.name,
    version: ENV.app.version,
    environment: ENV.app.environment,
    projectId: ENV.project.id,
  });
} catch (error) {
  logger.error('Environment validation failed', 'App', { error });
  // In production, you might want to show a user-friendly error page
}

// Create router
const router = createBrowserRouter(routes);

const App: React.FC = () => {
  // Log app initialization with environment info
  React.useEffect(() => {
    logger.info('Application initialized', 'App', {
      appName: ENV.app.name,
      version: ENV.app.version,
      environment: ENV.app.environment,
      projectId: ENV.project.id,
      isDevelopment: ENV.app.isDevelopment,
    });
    
    // Start performance monitoring
    performanceMonitor.startMark('app_load');
    
    return () => {
      const duration = performanceMonitor.endMark('app_load');
      if (duration) {
        logger.info(`App load completed`, 'App', { 
          loadTime: `${duration.toFixed(2)}ms` 
        });
      }
    };
  }, []);
  
  return (
    <HelmetProvider>
    <ThemeMasterProvider defaultTheme="light" defaultVariant="glass">
      <UserStateProvider>
        <GlobalErrorBoundary>
          <RouterProvider router={router} />
          <Toaster 
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                borderRadius: '1rem',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'var(--bc)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
                padding: '1rem 1.5rem',
              },
              success: {
                iconTheme: {
                  primary: 'var(--su)',
                  secondary: 'var(--b1)',
                },
              },
              error: {
                iconTheme: {
                  primary: 'var(--er)',
                  secondary: 'var(--b1)',
                },
              },
            }}
          />
        </GlobalErrorBoundary>
      </UserStateProvider>
    </ThemeMasterProvider>
    </HelmetProvider>
  );
};

export default App;
